/**
 * VAGUE 36 — Monthly commission dashboard card.
 *
 * Shows the current month's earnings in big, trend vs prev month,
 * rank among active telepros, and anonymized top 3 leaderboard.
 * Polls GET /api/telepro/me/commission-dashboard every 2 minutes.
 */
import React, { useEffect, useState, useCallback } from "react";
import { teleproApi } from "../../lib/teleproApi";
import { TrendUp, TrendDown, Trophy, Medal } from "@phosphor-icons/react";


function formatEur(n) {
  const v = Number(n || 0);
  return v.toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

const RANK_STYLES = {
  1: { bg: "bg-amber-100 text-amber-900 border-amber-300", medal: "🥇" },
  2: { bg: "bg-slate-100 text-slate-800 border-slate-300", medal: "🥈" },
  3: { bg: "bg-orange-100 text-orange-900 border-orange-300", medal: "🥉" },
};


export default function TeleproCommissionCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const r = await teleproApi.get("/me/commission-dashboard");
      setData(r?.data || null);
    } catch (_e) {
      // Silent — the card is nice-to-have, never block the portal.
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const id = setInterval(fetchData, 120_000);
    return () => clearInterval(id);
  }, [fetchData]);

  if (loading || !data) return null;

  const { this_month_eur, prev_month_eur, trend_pct, rank, total_ranked, top_3 } = data;
  const hasEarnings = this_month_eur > 0;
  const now = new Date();
  const monthLabel = now.toLocaleDateString("fr-FR", { month: "long", year: "numeric" });

  return (
    <div
      className="bg-gradient-to-br from-emerald-50 to-white border border-emerald-200 rounded-sm p-5 mb-4 shadow-sm"
      data-testid="telepro-commission-card"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        {/* Big € number + month */}
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-emerald-900/70 mb-1">
            Tes commissions · {monthLabel}
          </div>
          <div className="flex items-baseline gap-3 flex-wrap">
            <div className="font-display text-3xl sm:text-4xl font-semibold text-emerald-900 tabular-nums"
                 data-testid="commission-this-month">
              {formatEur(this_month_eur)} €
            </div>
            {trend_pct !== null && trend_pct !== undefined && (
              <div
                className={`inline-flex items-center gap-1 text-xs font-bold px-2 py-1 rounded-sm ${
                  trend_pct >= 0 ? "bg-emerald-100 text-emerald-800" : "bg-rose-100 text-rose-800"
                }`}
                data-testid="commission-trend"
              >
                {trend_pct >= 0 ? <TrendUp size={12} weight="bold" /> : <TrendDown size={12} weight="bold" />}
                {trend_pct >= 0 ? "+" : ""}{trend_pct}%
              </div>
            )}
          </div>
          <div className="text-xs text-slate-600 mt-1.5">
            Mois précédent&nbsp;: <span className="tabular-nums">{formatEur(prev_month_eur)} €</span>
            {!hasEarnings && (
              <span className="block mt-1 text-slate-500 italic">
                Aucune commission ce mois pour le moment — vos premières ventes apparaîtront ici en temps réel.
              </span>
            )}
          </div>
        </div>

        {/* Rank badge */}
        {rank && total_ranked > 1 && (
          <div
            className={`shrink-0 border rounded-sm px-3 py-2 ${(RANK_STYLES[rank] || RANK_STYLES[3]).bg}`}
            data-testid="commission-rank"
          >
            <div className="text-[10px] uppercase tracking-[0.15em] font-bold opacity-70">Classement</div>
            <div className="flex items-baseline gap-1 mt-0.5">
              <span className="text-2xl font-bold tabular-nums">
                {(RANK_STYLES[rank]?.medal) || `#${rank}`}
              </span>
              <span className="text-xs opacity-80">
                {rank <= 3 ? `(${rank}${rank === 1 ? "ᵉʳ" : "ᵉ"})` : `${rank}ᵉ`} sur {total_ranked}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Top 3 leaderboard */}
      {top_3 && top_3.length > 0 && (
        <div className="mt-4 pt-4 border-t border-emerald-100">
          <div className="flex items-center gap-2 mb-2">
            <Trophy size={14} weight="duotone" className="text-amber-700" />
            <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-700">
              Top 3 du mois
            </span>
          </div>
          <div className="space-y-1.5" data-testid="commission-top3">
            {top_3.map((t, i) => (
              <div
                key={i}
                className={`flex items-center justify-between gap-3 py-1.5 px-2 rounded-sm ${
                  t.is_me ? "bg-emerald-100 border border-emerald-300" : "bg-white border border-slate-100"
                }`}
                data-testid={`commission-top3-row-${i}`}
              >
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-sm shrink-0">
                    {["🥇", "🥈", "🥉"][i]}
                  </span>
                  <span className={`text-sm truncate ${t.is_me ? "font-bold text-emerald-900" : "text-slate-700"}`}>
                    {t.name} {t.is_me && <span className="text-xs">(vous)</span>}
                  </span>
                </div>
                <span className={`text-sm tabular-nums shrink-0 ${t.is_me ? "font-bold text-emerald-900" : "text-slate-600"}`}>
                  {formatEur(t.amount_eur)} €
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
